import { useAccessPendingAction } from "./use-access-pending-action";
import { Link } from "react-router";
import { Unlink } from "lucide-react";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { Tooltip } from "@/components/common/Tooltip";
import { useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Select } from "@/components/design/Select";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { AccessGroupRolesDocument, AccessGroupsDocument, AssignAccessGroupRoleDocument, RemoveAccessGroupRoleDocument,
  AccessScopeKind, type AccessGroupDetailsFragment } from "@/graphql/generated/operations";
import { isAdministratorGroup, groupLabel } from "./access-group-labels";
import { roleLabel } from "./access-role-labels";
import { accessScopeLabel } from "@/lib/access-scope-label";
import { userErrorMessage } from "@/lib/user-error";

export function AccessGroupRolesPanel({ group, onChanged }: {
  group: AccessGroupDetailsFragment; onChanged: () => Promise<void>;
}) {
  const pendingAction = useAccessPendingAction(`access-assignments-${group.id}`);
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { data, loading, error, refetch } = useQuery(AccessGroupRolesDocument, {
    variables: { groupId: group.id }, fetchPolicy: "network-only",
  });
  const groups = useQuery(AccessGroupsDocument, { fetchPolicy: "network-only" });
  const [roleId, setRoleId] = useState("");
  const [chosenScope, setScope] = useState<AccessScopeKind | "">("");
  const [targetGroup, setTargetGroup] = useState("");
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const inFlight = useRef(false);
  const [assign, { loading: assigning }] = useMutation(AssignAccessGroupRoleDocument);
  const [remove, { loading: removing }] = useMutation(RemoveAccessGroupRoleDocument);
  const role = data?.accessRoles.roles.find((item) => item.id === roleId);
  const scope = role?.supportedScopes.length === 1 ? role.supportedScopes[0]! : chosenScope;
  const busy = assigning || removing || pendingAction.pending;
  const assignedRoleLabel = (roleId: string, fallback: string) => {
    const assignedRole = data?.accessRoles.roles.find((role) => role.id === roleId);
    return assignedRole ? roleLabel(intl, assignedRole) : fallback;
  };
  const scopeLabel = (kind: AccessScopeKind) => t(`admin.groups.scope.${kind}`);

  async function change(assignmentId?: string) {
    if (inFlight.current || (!assignmentId && (!roleId || !scope || !role?.supportedScopes.includes(scope) || (scope === AccessScopeKind.GroupMembers && !targetGroup)))) return;
    inFlight.current = true;
    try {
      if (assignmentId) {
        await remove({ variables: { input: { groupId: group.id, assignmentId } } });
      } else if (scope) {
        await assign({ variables: { input: { groupId: group.id, roleId,
          scope: { kind: scope, groupId: scope === AccessScopeKind.GroupMembers ? targetGroup : null } } } });
      }
      setRemovingId(null);
      await Promise.all([refetch(), onChanged()]);
      setFailure(null);
    } catch (cause) { setFailure(userErrorMessage(intl, cause)); throw cause; }
    finally { inFlight.current = false; }
  }

  async function loadGroups() {
    const page = groups.data?.accessGroups;
    if (!page?.nextAfter || groups.loading || inFlight.current) return;
    inFlight.current = true;
    try {
      await groups.fetchMore({ variables: { after: page.nextAfter }, updateQuery(previous, { fetchMoreResult }) {
        const next = fetchMoreResult.accessGroups;
        const items = new Map(previous.accessGroups.groups.map((item) => [item.id, item]));
        next.groups.forEach((item) => items.set(item.id, item));
        return { ...previous, accessGroups: { ...next, groups: [...items.values()] } };
      } });
    } catch (cause) { setFailure(userErrorMessage(intl, cause)); }
    finally { inFlight.current = false; }
  }

  const scopeInfo = (kind: AccessScopeKind) => <InfoTooltip ariaLabel={t("admin.groups.scope")}>
    {t(`admin.groups.scopeHint.${kind}`)}
  </InfoTooltip>;
  const sortedRoles = [...(data?.accessRoles.roles ?? [])].sort((a, b) => roleLabel(intl, a).localeCompare(roleLabel(intl, b), intl.locale));
  return <div id="group-role-panel" className="@container space-y-5">
    {pendingAction.label && <p role="status" className="text-smycles-body font-medium">{pendingAction.label}</p>}
    <h3 className="text-smycles-body font-medium">{t("admin.groups.roles")}</h3>
    {(error || groups.error || failure) && <p role="alert" className="text-smycles-body text-ink">
      {failure ?? userErrorMessage(intl, error ?? groups.error)}</p>}
    {loading && <p role="status" className="text-smycles-body text-ink-muted">{t("common.loading")}</p>}
    {group.canManageRoles && <form id="group-role-form" className="space-y-3" onSubmit={(event) => { event.preventDefault(); if (role && scope) pendingAction.stage(`${t("admin.groups.assignRole")}: ${roleLabel(intl, role)} (${scopeLabel(scope)})`, () => change()); }}>
      <div className="grid items-end gap-4 @min-[36rem]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <label className="block min-w-0"><span className="smycles-field-label">{t("admin.groups.role")}</span>
          <Select id="group-role-select" value={roleId} required disabled={busy || loading} placeholder={t("admin.groups.chooseRole")}
            onChange={(value) => { setRoleId(value); setScope(""); setTargetGroup(""); }}
            options={sortedRoles.map(item => ({ value: item.id, label: roleLabel(intl, item) }))} /></label>
        {role && role.supportedScopes.length > 1 ? <label className="block min-w-0"><span className="smycles-field-label">{t("admin.groups.scope")}</span>
          <Select id="group-role-scope" value={scope} required disabled={busy} onChange={(value) => { setScope(value as AccessScopeKind); setTargetGroup(""); }}
            options={role.supportedScopes.map(kind => ({ value: kind, label: scopeLabel(kind) }))} /></label>
          : <div className="min-w-0"><span className="smycles-field-label">{t("admin.groups.scope")}</span>
            <div id="group-role-fixed-scope" className="flex min-h-10 items-center gap-2 text-smycles-body">
              {role && scope ? <><span>{scopeLabel(scope)}</span>{scopeInfo(scope)}</>
                : <span className="text-ink-muted">{t(role ? "admin.groups.noSupportedScope" : "admin.groups.chooseRoleFirst")}</span>}
            </div>
          </div>}
        <Button id="group-role-assign" type="submit" disabled={busy || !role || !scope || !role.supportedScopes.includes(scope)
          || (scope === AccessScopeKind.GroupMembers && !targetGroup)}>{t("admin.groups.assignRole")}</Button>
      </div>
      {role && role.supportedScopes.length > 1 && scope && <p className="text-smycles-meta text-ink-muted">{t(`admin.groups.scopeHint.${scope}`)}</p>}
      {scope === AccessScopeKind.GroupMembers && <label className="block">
        <span className="smycles-field-label">{t("admin.groups.scopeGroup")}</span>
        <Select id="group-role-target" value={targetGroup} required disabled={busy} onChange={setTargetGroup}
          options={groups.data?.accessGroups.groups.map(item => ({ value: item.id, label: groupLabel(intl, item) })) ?? []} />
      </label>}
      {scope === AccessScopeKind.GroupMembers && groups.data?.accessGroups.hasNextPage &&
        <Button variant="secondary" disabled={groups.loading || busy} onClick={() => void loadGroups()}>{t("common.loadMore")}</Button>}
    </form>}
    <ul id="group-role-assignments" className="divide-y divide-ink/10 border-t border-ink/10">{data?.accessGroupRoleAssignments.map(item => <li key={item.id}
      className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <Link id={`group-role-link-${item.id}`} className="text-smycles-body hover:underline focus-visible:ring-2 focus-visible:ring-ink-ui"
          to={`/admin/settings?section=accessRoles&roleId=${encodeURIComponent(item.roleId)}`}>{assignedRoleLabel(item.roleId, item.roleName)}</Link>
        <div className="mt-1 flex items-center gap-2 text-smycles-meta text-ink-muted"><span id={`group-role-assignment-${item.id}-scope`}>{accessScopeLabel({ ...item.scope,
          groupName: item.scope.groupId ? groupLabel(intl, { id: item.scope.groupId, name: item.scope.groupName ?? item.scope.groupId }) : null }, intl)}</span>{scopeInfo(item.scope.kind)}</div>
      </div>
      {(item.canRemove || isAdministratorGroup(group)) && <Tooltip content={t(item.canRemove ? "admin.groups.removeRole" : "admin.groups.lastAdministratorRole")}>
        <Button id={`group-role-remove-${item.id}`} variant="icon" size="compact" disabled={busy || !item.canRemove} aria-label={t("common.remove")}
          onClick={() => setRemovingId(item.id)}><Unlink size={16} aria-hidden="true" /></Button>
      </Tooltip>}
    </li>)}</ul>
    {!loading && data?.accessGroupRoleAssignments.length === 0 &&
      <p className="text-smycles-body text-ink-muted">{t("admin.groups.noRoles")}</p>}
    <ConfirmDialog id="group-role-remove-dialog" open={!!removingId} title={t("admin.groups.removeRole")}
      message={t("admin.groups.removeRoleHint")} onCancel={() => { if (!busy) setRemovingId(null); }}
      onConfirm={() => { if (removingId) { const id = removingId; const item = data?.accessGroupRoleAssignments.find(item => item.id === id); pendingAction.stage(`${t("admin.groups.removeRole")}: ${item ? assignedRoleLabel(item.roleId, item.roleName) : ""}`, () => change(id)); setRemovingId(null); } }} />
  </div>;
}
